import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { mockFinalQuiz } from '@/data/finalQuizMock';
import { QuizProgress, QuizResult } from '@/types/finalQuiz';

export default function FinalQuizPage() {
  const { trailId } = useParams<{ trailId: string }>();
  const navigate = useNavigate();
  const quiz = mockFinalQuiz;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResult, setShowResult] = useState(false);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const selectedAnswer = answers[currentQuestion.id];
  const isLastQuestion = currentQuestionIndex === quiz.totalQuestions - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  const progress: QuizProgress = {
    currentQuestion: currentQuestionIndex + 1,
    totalQuestions: quiz.totalQuestions,
    answers,
    percentageComplete: ((currentQuestionIndex + 1) / quiz.totalQuestions) * 100,
  };

  const handleOptionSelect = (optionId: string) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: optionId,
    }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Calcular resultado
      calculateResult();
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstQuestion) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const calculateResult = () => {
    let correctCount = 0;
    const answeredQuestions: QuizResult['answeredQuestions'] = {};

    quiz.questions.forEach((question) => {
      const selectedOptionId = answers[question.id];
      const selectedOption = question.options.find((opt) => opt.id === selectedOptionId);
      const isCorrect = selectedOption?.isCorrect || false;

      if (isCorrect) {
        correctCount++;
      }

      answeredQuestions[question.id] = {
        questionId: question.id,
        selectedOptionId: selectedOptionId || '',
        isCorrect,
      };
    });

    const score = (correctCount / quiz.totalQuestions) * 100;
    const passed = score >= quiz.passingScore;

    const result: QuizResult = {
      passed,
      score,
      correctAnswers: correctCount,
      totalQuestions: quiz.totalQuestions,
      answeredQuestions,
    };

    setQuizResult(result);
    setShowResult(true);
  };

  const handleClose = () => {
    if (showResult && quizResult?.passed) {
      // Se passou no quiz, vai para o certificado
      navigate(`/trilhas/${trailId}/certificado`);
    } else {
      // Caso contrário, volta para a trilha
      navigate(`/trilhas/${trailId}`);
    }
  };

  const handleRetry = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResult(false);
    setQuizResult(null);
  };

  if (showResult && quizResult) {
    return (
      <div className="min-h-screen bg-surface-primary text-content-primary flex flex-col">
        <header className="flex items-center justify-between border-b border-white/10 bg-surface-primary/80 backdrop-blur-md px-6 md:px-10 py-3">
          <div className="flex items-center gap-4">
            <div className="size-6 text-accent">
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M24 4C25.7818 14.2173 33.7827 22.2182 44 24C33.7827 25.7818 25.7818 33.7827 24 44C22.2182 33.7827 14.2173 25.7818 4 24C14.2173 22.2182 22.2182 14.2173 24 4Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <h2 className="text-content-primary text-lg font-bold tracking-tight">h.lab</h2>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center rounded-xl size-10 bg-white/5 hover:bg-white/10 text-content-primary transition-all"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <main className="flex-1 flex flex-col items-center justify-center p-6 md:p-10">
          <div className="max-w-2xl w-full flex flex-col gap-8 text-center">
            <div className="flex flex-col items-center gap-6">
              <div
                className={`size-24 rounded-full flex items-center justify-center ${
                  quizResult.passed
                    ? 'bg-green-500/10 border-2 border-green-500'
                    : 'bg-red-500/10 border-2 border-red-500'
                }`}
              >
                <span
                  className={`material-symbols-outlined text-5xl material-fill ${
                    quizResult.passed ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {quizResult.passed ? 'check_circle' : 'cancel'}
                </span>
              </div>

              <div>
                <h1 className="text-content-primary text-3xl font-bold mb-2">
                  {quizResult.passed ? 'Parabéns! Você foi aprovado!' : 'Não foi dessa vez...'}
                </h1>
                <p className="text-content-muted text-lg">
                  {quizResult.passed
                    ? 'Você demonstrou excelente compreensão do conteúdo!'
                    : 'Continue estudando e tente novamente.'}
                </p>
              </div>
            </div>

            <div className="bg-surface-card border border-white/10 rounded-3xl p-8 flex flex-col gap-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-content-muted text-sm font-medium">Pontuação</span>
                  <span className="text-content-primary text-3xl font-bold">{Math.round(quizResult.score)}%</span>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-content-muted text-sm font-medium">Acertos</span>
                  <span className="text-content-primary text-3xl font-bold">
                    {quizResult.correctAnswers}/{quizResult.totalQuestions}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-content-muted text-sm font-medium">Aprovação</span>
                  <span className="text-content-primary text-3xl font-bold">{quiz.passingScore}%</span>
                </div>
              </div>

              <div className="h-3 w-full bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    quizResult.passed ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]' : 'bg-red-500'
                  }`}
                  style={{ width: `${quizResult.score}%` }}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              {!quizResult.passed && (
                <button
                  onClick={handleRetry}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-10 h-14 rounded-xl border border-white/10 text-content-primary font-bold hover:bg-white/5 transition-all"
                >
                  <span className="material-symbols-outlined">refresh</span>
                  Tentar Novamente
                </button>
              )}
              <button
                onClick={handleClose}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-10 h-14 rounded-xl bg-accent text-accent-contrast font-bold shadow-lg shadow-accent/20 hover:bg-accent-hover transition-all"
              >
                {quizResult.passed ? 'Ver Certificado' : 'Voltar à Trilha'}
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>
          </div>
        </main>

        <footer className="p-6 text-center">
          <p className="text-content-muted text-xs font-medium">
            {quizResult.passed
              ? 'Seu certificado de conclusão está sendo gerado e será enviado por e-mail.'
              : 'Revise o conteúdo das aulas e tente novamente quando se sentir preparado.'}
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-primary text-content-primary flex flex-col">
      <header className="flex items-center justify-between border-b border-white/10 bg-surface-primary/80 backdrop-blur-md px-6 md:px-10 py-3 sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <div className="size-6 text-accent">
            <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M24 4C25.7818 14.2173 33.7827 22.2182 44 24C33.7827 25.7818 25.7818 33.7827 24 44C22.2182 33.7827 14.2173 25.7818 4 24C14.2173 22.2182 22.2182 14.2173 24 4Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h2 className="text-content-primary text-lg font-bold tracking-tight">h.lab</h2>
        </div>
        <div className="flex items-center gap-6">
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-content-secondary">
            <span className="text-content-primary">{quiz.title.split(': ')[1]}</span>
            <span className="w-1 h-1 bg-white/20 rounded-full" />
            <span>Quiz Final</span>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center rounded-xl size-10 bg-white/5 hover:bg-white/10 text-content-primary transition-all"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-6 md:p-10">
        <div className="max-w-3xl w-full flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-end">
              <div>
                <span className="text-cyan-accent text-xs font-bold uppercase tracking-widest">Etapa Final</span>
                <h1 className="text-content-primary text-2xl font-bold">
                  Pergunta {progress.currentQuestion} de {progress.totalQuestions}
                </h1>
              </div>
              <span className="text-content-muted text-sm font-medium">
                {Math.round(progress.percentageComplete)}% Concluído
              </span>
            </div>
            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-cyan-accent shadow-[0_0_10px_rgba(0,245,255,0.5)] rounded-full transition-all"
                style={{ width: `${progress.percentageComplete}%` }}
              />
            </div>
          </div>

          <div className="bg-surface-card border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl">
            <div className="flex flex-col gap-8">
              <div className="space-y-4">
                <p className="text-content-muted text-sm font-semibold uppercase tracking-wider">
                  Módulo: {currentQuestion.module}
                </p>
                <h3 className="text-content-primary text-xl md:text-2xl font-bold leading-relaxed">
                  {currentQuestion.question}
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {currentQuestion.options.map((option) => {
                  const isSelected = selectedAnswer === option.id;
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleOptionSelect(option.id)}
                      className={`option-card flex items-start gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 text-left transition-all group ${
                        isSelected ? 'option-selected' : ''
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 size-6 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                          isSelected
                            ? 'border-cyan-accent text-cyan-accent'
                            : 'border-white/20 text-content-muted group-hover:border-cyan-accent/50 group-hover:text-cyan-accent'
                        }`}
                      >
                        {option.letter}
                      </div>
                      <p
                        className={`text-base font-medium transition-all ${
                          isSelected ? 'text-content-primary font-bold' : 'text-content-secondary'
                        }`}
                      >
                        {option.text}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <button
              onClick={handlePrevious}
              disabled={isFirstQuestion}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 h-12 rounded-xl border border-white/10 text-content-muted font-bold hover:bg-white/5 hover:text-content-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              Anterior
            </button>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={handleNext}
                disabled={!selectedAnswer}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-10 h-14 rounded-xl bg-accent text-accent-contrast font-bold shadow-lg shadow-accent/20 hover:bg-accent-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLastQuestion ? 'Finalizar' : 'Próximo'}
                <span className="material-symbols-outlined">
                  {isLastQuestion ? 'check_circle' : 'arrow_forward'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </main>

      <footer className="p-6 text-center">
        <p className="text-content-muted text-xs font-medium">
          Certifique-se de revisar suas respostas. O resultado final será gerado após o envio da última questão.
        </p>
      </footer>
    </div>
  );
}
